import React, { useCallback, useMemo } from 'react';
import { Avatar } from 'design-system/avatar';
import { CIRCLE_BORDER_SIZE, CIRCLE_SIZE } from 'design-system/LogoBankAccount';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import { IconTrash } from 'design-system/icons';
import Text from 'design-system/Text';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { StyleSheet, View } from 'react-native';
import { Indicator, Spacer } from 'design-system';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import { useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import Modal from 'utils/packages/Modal';
import IconStatusPending from 'design-system/icons/status/IconStatusPending';
import IconStatusCancelled from 'design-system/icons/status/IconStatusCancelled';
import { isWeb } from '../../../constants';
import { useManageSpaceInvite } from '../hooks';
const SpaceMemberInvited = ({ spaceId, spaceName, spaceCreatedBy, invite, member, status, }) => {
    const { id, handle, firstName, lastName, imageUrl } = member;
    const navigation = useMainStackNavigation();
    const myId = useAppSelector(selectUserId);
    const colors = useColors();
    const { cancelInvite, fetchingCancelInvite: fetching } = useManageSpaceInvite(spaceId, invite.id, id, handle);
    const isSpaceOwner = myId === spaceCreatedBy;
    const [name, desc, firstLetter, lastLetter, statusText] = useMemo(() => [
        firstName && lastName ? `${firstName} ${lastName}` : handle,
        `@${handle}`,
        firstName ?? '?',
        lastName ?? '',
        status === 'PENDING' ? 'Invitation pending' : 'Invitation rejected',
    ], [firstName, handle, lastName, status]);
    const leftIcon = (<Avatar name={firstLetter} lastName={lastLetter} size={rem(CIRCLE_SIZE)} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} imageUrl={imageUrl} borderWidth={CIRCLE_BORDER_SIZE} medium/>);
    const onCancelInvite = useCallback(() => {
        if (!fetching) {
            Modal.showConfirmation({
                title: 'Are you sure?',
                subtitle: 'Do you want to cancel invitation?',
                confirmButtonTitle: 'No, thanks',
                cancelButtonTitle: 'Yes, let me cancel',
                onPressCancel: () => cancelInvite(),
            });
        }
    }, [cancelInvite, fetching]);
    const onPress = useCallback(() => {
        navigation.navigateDeprecated('ManageSpaceInvite', {
            spaceId,
            inviteId: invite.id,
            status,
            firstName,
            lastName,
            imageUrl,
            spaceName,
            handle,
            memberId: id,
        });
    }, [
        firstName,
        handle,
        id,
        imageUrl,
        invite.id,
        lastName,
        spaceId,
        spaceName,
        status,
    ]);
    const inviteStatusContent = useMemo(() => (<View style={styles.row}>
        {status === 'PENDING' ? (<IconStatusPending scale={0.8} color={colors.cards.onDark} bgColor={colors.elements.statusInformational}/>) : (<IconStatusCancelled color={colors.elements.negative} scale={0.8}/>)}
        <Spacer.W8 />
        <Text TextThin-14 color={status === 'PENDING'
            ? colors.text.statusInformational
            : colors.text.negative} numberOfLines={1}>
          {statusText}
        </Text>
      </View>), [
        status,
        colors.cards.onDark,
        colors.elements.statusInformational,
        colors.elements.negative,
        colors.text.statusInformational,
        colors.text.negative,
        statusText,
    ]);
    return (<TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={onPress} disabled={!isSpaceOwner}>
      <View style={styles.container}>
        <View style={[styles.iconContainerCircle]}>{leftIcon}</View>
        <Spacer.W16 />
        <View style={styles.contentContainer}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16 numberOfLines={1}>
                {name}
              </Text>
              <Text TextThin-14 Gray numberOfLines={1}>
                {desc}
              </Text>
              {inviteStatusContent}
            </View>
            <Spacer.W8 />
            {isSpaceOwner ? (<TouchableOpacity hoverOnDark onPress={onCancelInvite} testID="invite-cancel-button">
                {fetching ? (<Indicator dark/>) : (<IconTrash color={colors.elements.secondary}/>)}
              </TouchableOpacity>) : null}
          </View>
        </View>
      </View>
    </TouchableHighlight>);
};
const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    container: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            paddingLeft: rem(18),
        }),
    },
    contentContainer: { flex: 1 },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            marginRight: rem(10),
        }),
    },
    iconContainerCircle: {
        alignItems: 'center',
        borderRadius: rem(22),
        width: rem(CIRCLE_SIZE),
        justifyContent: 'center',
        height: rem(CIRCLE_SIZE),
    },
});
export default SpaceMemberInvited;
